import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  LabelList
} from "recharts";
import styles from "./css/BarsGraph.module.css";
import { LoadingWaves } from '../addons/LoadingWaves';

interface ChargingProviderData {
  [year: string]: {
    "מספר רכבים": number;
    "הספק עבור טעינה": number;
  };
}

interface ChartDataItem {
  year: string;
  vehicles: number;
  power: number;
  vehiclesLabel: string;
  powerLabel: string;
}

const COLORS: { [key: string]: string } = {
  "מספר רכבים": "#50C878",
  "הספק עבור טעינה": "#4A90E2"
};

interface ChargingPowerGraphCityProps {
  data: ChargingProviderData | any;
  size?: 'small' | 'normal';
}

export const ChargingPowerGraphCity: React.FC<ChargingPowerGraphCityProps> = ({ data, size = 'normal' }) => {
  const formatNumber = (value: number) => {
    if (typeof value !== 'number') return value;
    return value.toLocaleString();
  };

  const getValueByPartialKey = (obj: any, partialKey: string) => {
    if (!obj) return 0;
    const key = Object.keys(obj).find(k => k.replace(/\s/g, '').includes(partialKey));
    return key ? obj[key] : 0;
  };

  // Dynamically extract years from data
  const years = data ? Object.keys(data).sort() : [];
  const firstYear = years[0] || "2025";
  const secondYear = years[1] || "2040";

  const transformedData: ChartDataItem[] = data ? [
    {
      year: firstYear,
      vehicles: getValueByPartialKey(data[firstYear], "רכבים"),
      power: data[firstYear]?.["הספק עבור טעינה"] || 0,
      vehiclesLabel: "מספר רכבים",
      powerLabel: "הספק"
    },
    {
      year: secondYear,
      vehicles: getValueByPartialKey(data[secondYear], "רכבים"),
      power: data[secondYear]?.["הספק עבור טעינה"] || 0,
      vehiclesLabel: "מספר רכבים",
      powerLabel: "הספק"
    }
  ] : [];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white p-2 border border-gray-200 rounded shadow-sm">
        <p className="text-sm font-medium mb-1">{label}</p>
        {payload.map((item: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.fill }} />
            <span>{item.name}:</span>
            <span className="font-medium">{formatNumber(item.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  if (!data) {
    return (
      <div className={styles.container}>
        <LoadingWaves color={Object.values(COLORS)[0]} />
      </div>
    );
  }

  const maxValue = Math.max(...transformedData.map(item => Math.max(item.vehicles, item.power)));
  const displayMaxValue = Math.ceil(maxValue * 1.1);
  const fontSize = size === 'small' ? 10 : 12;

  return (
    <div className={styles.container}>
      <div className="w-full h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={transformedData}
            margin={{ top: 20, right: 20, left: -40, bottom: 0 }}
            barGap={4}
            barCategoryGap={25}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" />
            <XAxis
              dataKey="year"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6C7693', fontSize }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6C7693', fontSize: fontSize - 1 }}
              tickFormatter={formatNumber}
              domain={[0, displayMaxValue]}
            />
            <Tooltip content={CustomTooltip} />
            <Bar dataKey="vehicles" name="מספר רכבים" fill={COLORS["מספר רכבים"]} radius={[3, 3, 0, 0]} barSize={28}>
              <LabelList dataKey="vehiclesLabel" position="top" fill="#6C7693" fontSize={fontSize} offset={5} />
            </Bar>
            <Bar dataKey="power" name="הספק עבור טעינה" fill={COLORS["הספק עבור טעינה"]} radius={[3, 3, 0, 0]} barSize={28}>
              <LabelList dataKey="powerLabel" position="top" fill="#6C7693" fontSize={fontSize} offset={5} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
